import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  useMantineColorScheme,
  Skeleton,
  ActionIcon,
  Paper,
  ScrollArea,
  Center,
  ThemeIcon,
  Avatar,
  SegmentedControl,
  Select,
  Checkbox
} from '@mantine/core';
import SearchInput from '../components/ui/SearchInput';
import { useQuery } from '@tanstack/react-query';
import { notificationsQuery } from '../queries/appQueries';
import {
  IconCheck,
  IconRefresh,
  IconBellOff,
  IconMessage2,
  IconAlertTriangle,
  IconSettings,
  IconCreditCard,
  IconCalendarEvent,
  IconClock,
  IconPoint,
  IconStar,
  IconStarFilled,
  IconUsers,
  IconArrowRight
} from '@tabler/icons-react';
import { notifications as mantineNotifications } from '@mantine/notifications';
import { notificationService, type NotificationDTO } from '../services/notificationService';
import { MultijugadorService } from '../services/multijugadorService';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UiNotification {
  id: number;
  title: string;
  description: string;
  time: Date;
  type: 'foro' | 'sistema' | 'examen' | 'sistema' | 'pago' | 'alerta';
  unread: boolean;
  saved?: boolean;
  metadata?: Record<string, any>;
  creador?: {
    id: number;
    username: string;
    nombre?: string;
    apellidos?: string;
    email: string;
  };
}

const mapDtoToUi = (dto: NotificationDTO): UiNotification => {
  const tipo = dto.tipo.toLowerCase() as UiNotification['type'];
  return {
    id: dto.id,
    title: dto.titulo,
    description: dto.mensaje,
    type: (['foro','examen','pago','alerta','sistema'].includes(tipo) ? tipo : 'sistema') as UiNotification['type'],
    time: new Date(dto.creadoEn),
    unread: !dto.leida,
    saved: dto.guardada ?? false,
    metadata: dto.metadata,
    creador: dto.creador,
  };
};

const typeToBadge = (type: UiNotification['type']) => {
  switch (type) {
    case 'foro':
      return { label: 'Foro', color: 'gray', icon: <IconMessage2 size={12} /> };
    case 'examen':
      return { label: 'Examen', color: 'gray', icon: <IconCalendarEvent size={12} /> };
    case 'pago':
      return { label: 'Pago', color: 'gray', icon: <IconCreditCard size={12} /> };
    case 'alerta':
      return { label: 'Alerta', color: 'gray', icon: <IconAlertTriangle size={12} /> };
    case 'sistema':
    default:
      return { label: 'Sistema', color: 'gray', icon: <IconSettings size={12} /> };
  }
};

const NotificacionesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Notificación a resaltar al llegar desde la campanita (?n=id).
  const highlightId = searchParams.get('n') ? Number(searchParams.get('n')) : null;
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const { colorScheme } = useMantineColorScheme();
  const [notifications, setNotifications] = useState<UiNotification[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  // UI state
  const [tab, setTab] = useState<'all' | 'unread' | 'saved' | 'read'>('unread');
  const [query, setQuery] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'type'>('none');
  const [typeFilter, setTypeFilter] = useState<'all' | 'sistema' | 'foro' | 'examen' | 'pago' | 'alerta'>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Notificaciones cacheadas con React Query (paint instantáneo al revisitar; siempre revalida).
  // Se vuelcan a estado local para conservar las actualizaciones optimistas (marcar leída, guardar…).
  const notisQ = useQuery(notificationsQuery());
  const loading = notisQ.isLoading;
  useEffect(() => {
    if (notisQ.data) setNotifications(notisQ.data.map(mapDtoToUi));
  }, [notisQ.data]);

  // Al llegar con ?n=id (desde la campanita): mostrar todas, resaltar esa notif,
  // hacer scroll hacia ella y quitar el resaltado tras unos segundos.
  useEffect(() => {
    if (!highlightId || notifications.length === 0) return;
    if (!notifications.some(n => n.id === highlightId)) return;
    setTab('all');
    setHighlighted(highlightId);
    const scrollT = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clearT = setTimeout(() => setHighlighted(null), 2800);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
  }, [highlightId, notifications]);

  const unreadCount = useMemo(() => notifications.filter(n => n.unread).length, [notifications]);

  const filtered = useMemo(() => {
    let result = notifications;

    // Tabs
    if (tab === 'saved') result = result.filter(n => n.saved);
    if (tab === 'read') result = result.filter(n => !n.unread);
    if (tab === 'unread') result = result.filter(n => n.unread);


    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter(n => n.type === typeFilter);
    }

    // Search
    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter(n =>
        n.title.toLowerCase().includes(q) ||
        n.description.toLowerCase().includes(q)
      );
    }

    // Sort newest first
    result = result.sort((a, b) => b.time.getTime() - a.time.getTime());
    return result;
  }, [notifications, query, tab, typeFilter]);

  const refreshNotifications = async () => {
    setRefreshing(true);
    try {
      const data = await notificationService.getMyNotifications(0, 50);
      setNotifications(data.map(mapDtoToUi));
      mantineNotifications.show({
        title: 'Actualizado',
        message: 'Notificaciones actualizadas correctamente',
        color: 'green',
        icon: <IconCheck size={16} />
      });
    } catch (e) {
      mantineNotifications.show({
        title: 'Error',
        message: 'Error al actualizar notificaciones',
        color: 'red'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const toggleRead = async (id: number) => {
    try {
      const target = notifications.find(n => n.id === id);
      if (!target) return;

      if (target.unread) {
        await notificationService.markAsRead(id);
      }
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, unread: !n.unread } : n));
      // Feedback
      mantineNotifications.show({ title: 'Listo', message: target.unread ? 'Marcada como leída' : 'Marcada como no leída', color: 'green' });
    } catch (e) {
      console.error('Error cambiando estado de notificación:', e);
      mantineNotifications.show({
        title: 'Error',
        message: 'Error al actualizar la notificación',
        color: 'red'
      });
    }
  };

  const toggleSaved = async (id: number) => {
    try {
      const target = notifications.find(n => n.id === id);
      if (!target) return;
      const next = !(target.saved ?? false);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, saved: next } : n));
      await notificationService.setSaved(id, next);
    } catch (e) {
      console.error('Error guardando notificación:', e);
      mantineNotifications.show({ title: 'Error', message: 'No se pudo actualizar el estado de guardado', color: 'red' });
    }
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id); else copy.add(id);
      return copy;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkMarkAsRead = async () => {
    const ids = filtered.filter(n => selected.has(n.id) && n.unread).map(n => n.id);
    if (ids.length === 0) return;
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, unread: false } : n));
    try {
      await Promise.all(ids.map(id => notificationService.markAsRead(id)));
      mantineNotifications.show({ title: 'Listo', message: `${ids.length} marcadas como leídas`, color: 'green' });
    } catch {
      mantineNotifications.show({ title: 'Error', message: 'No se pudieron marcar como leídas', color: 'red' });
    } finally {
      clearSelection();
    }
  };

  const bulkSave = async (valor: boolean) => {
    const ids = filtered.filter(n => selected.has(n.id)).map(n => n.id);
    if (ids.length === 0) return;
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, saved: valor } : n));
    try {
      await Promise.all(ids.map(id => notificationService.setSaved(id, valor)));
      mantineNotifications.show({ title: 'Listo', message: valor ? 'Guardadas' : 'Quitadas de guardados', color: 'green' });
    } catch {
      mantineNotifications.show({ title: 'Error', message: 'No se pudo actualizar guardados', color: 'red' });
    } finally {
      clearSelection();
    }
  };

  const bulkMarkAsUnread = async () => {
    const ids = filtered.filter(n => selected.has(n.id) && !n.unread).map(n => n.id);
    if (ids.length === 0) return;
    // Solo cliente por ahora (no hay endpoint para marcar como no leída)
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, unread: true } : n));
    mantineNotifications.show({ title: 'Listo', message: `${ids.length} notificaciones marcadas como no leídas`, color: 'green' });
    clearSelection();
  };

  const allSelected = filtered.length > 0 && filtered.every(n => selected.has(n.id));

  return (
    <Box
      style={{
        flex: 1,
        minHeight: '100vh',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : '#f7f3ee',
        padding: '16px 24px',
        overflow: 'hidden',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box mb="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <Box>
            <Title
              order={2}
              size="1.3rem"
              fw={600}
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                fontFamily: 'Space Grotesk, Inter, sans-serif',
                marginBottom: '4px',
              }}
            >
              Notificaciones
            </Title>
            <Text
              size="xs"
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {notifications.length} anuncios • {unreadCount} nuevos
            </Text>
          </Box>
          <Group gap="sm" wrap="wrap">
            <SearchInput
              placeholder="Buscar notificaciones..."
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              size="sm"
            />

            <Select
              value={groupBy}
              onChange={(v) => setGroupBy((v as any) ?? 'none')}
              data={[{ label: 'Agrupar: Ninguno', value: 'none' }, { label: 'Agrupar: Tipo', value: 'type' }]}
              size="sm"
              radius="xl"
              styles={{
                input: {
                  background: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.3)'
                    : 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#1f2937',
                  '&:focus': {
                    background: colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  }
                },
                dropdown: {
                  background: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.9)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                }
              }}
            />

            <Select
              value={typeFilter}
              onChange={(v) => setTypeFilter((v as any) ?? 'all')}
              data={[
                { label: 'Tipo: Todos', value: 'all' },
                { label: 'Alerta', value: 'alerta' },
                { label: 'Sistema', value: 'sistema' },
                { label: 'Examen', value: 'examen' },
                { label: 'Pago', value: 'pago' },
                { label: 'Foro', value: 'foro' },
              ]}
              size="sm"
              radius="xl"
              styles={{
                input: {
                  background: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.3)'
                    : 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#1f2937',
                  '&:focus': {
                    background: colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  }
                },
                dropdown: {
                  background: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.9)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                }
              }}
            />

            <ActionIcon
              variant="subtle"
              size="lg"
              radius="xl"
              onClick={refreshNotifications}
              loading={refreshing}
              style={{
                background: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.4)'
                  : 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1f2937',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.6)'
                    : 'rgba(255, 255, 255, 0.6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                }
              }}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      {/* Content Container */}
      <Box>
        {/* Tabs and bulk actions */}
        <Group justify="space-between" align="center" mb="sm">
          <SegmentedControl
            value={tab}
            onChange={(v) => { setTab(v as any); clearSelection(); }}
            data={[
              { label: 'Todas', value: 'all' },
              { label: 'No leídas', value: 'unread' },
              { label: 'Guardadas', value: 'saved' },
              { label: 'Leídas', value: 'read' }
            ]}
            styles={{
              root: {
                background: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.3)'
                  : 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '4px',
              },
              control: {
                border: 'none !important',
                '&[data-active="true"]': {
                  background: colorScheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                }
              },
              label: {
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1f2937',
                fontWeight: 500,
                '&[data-active="true"]': {
                  color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                }
              }
            }}
          />

          <Group gap="md" align="center">
            <Checkbox
              checked={allSelected}
              indeterminate={selected.size > 0 && !allSelected}
              onChange={() => {
                if (allSelected) {
                  clearSelection();
                } else {
                  setSelected(new Set(filtered.map(n => n.id)));
                }
              }}
              label="Seleccionar todo"
            />
          {selected.size > 0 && (
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                onClick={bulkMarkAsRead}
                leftSection={<IconCheck size={14} />}
                disabled={notifications.filter(n => selected.has(n.id) && n.unread).length === 0}
              >
                Marcar como leídas
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={bulkMarkAsUnread}
                disabled={notifications.filter(n => selected.has(n.id) && !n.unread).length === 0}
              >
                Marcar como no leídas
              </Button>
              <Button size="xs" variant="light" onClick={() => bulkSave(true)} leftSection={<IconStar size={14} />}>Guardar</Button>
              <Button size="xs" variant="subtle" onClick={() => bulkSave(false)}>Quitar de guardados</Button>
              <Button size="xs" variant="default" onClick={clearSelection}>Limpiar selección</Button>
            </Group>
          )}
          </Group>
        </Group>

        {/* Lista compacta de notificaciones */}
        <ScrollArea h="calc(100vh - 220px)" scrollbarSize={4} style={{ background: 'transparent' }}>
        <Stack gap="sm">
          {loading ? (
            <Stack gap="md">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={72} radius="lg" />
              ))}
            </Stack>
          ) : filtered.length === 0 ? (
            <Paper
              p="md"
              radius="lg"
              style={{
                background: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.3)'
                  : 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Center>
                <Stack align="center" gap="md">
                  <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                    <IconBellOff size={30} />
                  </ThemeIcon>
                  <Box ta="center">
                    <Title order={3} c="dimmed" fw={600} mb="xs">
                      {tab === 'unread' ? 'Todo al día' : 'Sin anuncios'}
                    </Title>
                    <Text size="sm" c="dimmed">
                      {tab === 'unread'
                        ? 'No tienes anuncios nuevos por revisar'
                        : 'Los nuevos anuncios aparecerán aquí'
                      }
                    </Text>
                  </Box>
                </Stack>
              </Center>
            </Paper>
          ) : (
            // Optional grouping by type
            (
              groupBy === 'type'
                ? (Object.entries(
                    filtered.reduce<Record<string, UiNotification[]>>((acc, n) => {
                      const key = n.type;
                      (acc[key] = acc[key] || []).push(n);
                      return acc;
                    }, {})
                  ) as [string, UiNotification[]][])
                : ([['all', filtered]] as [string, UiNotification[]][])
            ).map(([groupName, items]) => (
              <Stack key={groupName} gap="sm">
                {groupBy === 'type' && (
                  <Text size="sm" c="dimmed" pl="xs">{groupName.toUpperCase()}</Text>
                )}
                {items.map((notification: UiNotification) => {
                  const meta = typeToBadge(notification.type);
                  const timeAgo = notification.time.toLocaleDateString('es', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              });
              
              return (
                <Paper
                  key={notification.id}
                  ref={notification.id === highlighted ? highlightRef : undefined}
                  p="md"
                  radius="lg"
                  style={{
                    backdropFilter: 'blur(20px)',
                    border: notification.id === highlighted
                      ? '2px solid rgb(196, 213, 70)'
                      : notification.unread
                      ? `1px solid rgba(255, 255, 255, 0.3)`
                      : `1px solid rgba(255, 255, 255, 0.1)`,
                    borderLeft: notification.id === highlighted
                      ? '6px solid rgb(196, 213, 70)'
                      : notification.unread ? '4px solid rgba(255, 255, 255, 0.5)' : '4px solid transparent',
                    transition: 'all 0.25s ease',
                    cursor: 'pointer',
                    background: notification.id === highlighted
                      ? (colorScheme === 'dark' ? 'rgba(196, 213, 70, 0.12)' : 'rgba(196, 213, 70, 0.14)')
                      : colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.4)'
                      : 'rgba(255, 255, 255, 0.4)',
                    boxShadow: notification.id === highlighted
                      ? '0 0 0 4px rgba(196, 213, 70, 0.45)'
                      : notification.unread
                      ? '0 8px 32px rgba(0, 0, 0, 0.2)'
                      : `0 4px 16px rgba(0, 0, 0, 0.1)`,
                  }}
                >
                  <Group justify="space-between" align="flex-start" gap="md">
                    <Group gap="md" style={{ flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={selected.has(notification.id)}
                        onChange={() => toggleSelect(notification.id)}
                        onClick={(e) => e.stopPropagation()}
                        styles={{ input: { cursor: 'pointer' } }}
                      />
                      <Avatar
                        size={40}
                        radius="md"
                        color={meta.color}
                        style={{
                          flexShrink: 0,
                        }}
                      >
                        {notification.creador?.nombre
                          ? notification.creador.nombre.charAt(0).toUpperCase()
                          : 'A'
                        }
                      </Avatar>
                      
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" align="center">
                          <Badge
                            size="xs"
                            variant="light"
                            color={meta.color}
                            leftSection={meta.icon}
                          >
                            {meta.label}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {notification.creador?.nombre
                              ? `${notification.creador.nombre}${notification.creador.apellidos ? ' ' + notification.creador.apellidos : ''}`
                              : 'Administrador'
                            }
                          </Text>
                          {notification.unread && (
                            <Badge size="xs" color="gray" variant="light">
                              Nuevo
                            </Badge>
                          )}
                        </Group>
                        
                        <Text
                          fw={600}
                          size="sm"
                          c={colorScheme === 'dark' ? 'gray.1' : 'gray.9'}
                          style={{ lineHeight: 1.4 }}
                        >
                          {notification.title}
                        </Text>
                        
                        <Text
                          size="sm"
                          c={colorScheme === 'dark' ? 'gray.4' : 'gray.6'}
                          style={{
                            lineHeight: 1.5,
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                          }}
                        >
                          {notification.description}
                        </Text>

                        {/* Botón para unirse a sala si es una invitación */}
                        {notification.metadata?.codigoSala && (() => {
                          const codigoSala = notification.metadata!.codigoSala;
                          return (
                            <Button
                              variant="light"
                              color="blue"
                              size="xs"
                              leftSection={<IconUsers size={14} />}
                              rightSection={<IconArrowRight size={14} />}
                              mt="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // Intentar unirse automáticamente
                                  const sala = await MultijugadorService.unirseASala({ codigoSala });
                                  // Navegar a la sala con los datos
                                  navigate(`/multijugador/sala/${codigoSala}`, {
                                    state: { sala, fromInvitation: true }
                                  });
                                  toggleRead(notification.id);
                                  mantineNotifications.show({
                                    title: '¡Te has unido!',
                                    message: `Te uniste exitosamente a ${sala.nombreSala}`,
                                    color: 'green',
                                  });
                                } catch (error: any) {
                                  console.error('Error al unirse a sala:', error);
                                  mantineNotifications.show({
                                    title: 'Error',
                                    message: error.message || 'No se pudo unir a la sala',
                                    color: 'red',
                                  });
                                }
                              }}
                            >
                              Unirse a la Sala
                            </Button>
                          );
                        })()}

                        <Group gap="xs" align="center" mt="xs">
                          <IconClock size={12} color="gray" />
                          <Text size="xs" c="dimmed">
                            {timeAgo}
                          </Text>
                        </Group>
                      </Stack>
                    </Group>
                    
                    <Group gap="xs" align="flex-start" style={{ flexShrink: 0 }}>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        radius="xl"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRead(notification.id);
                        }}
                        title={notification.unread ? 'Marcar como leída' : 'Marcar como no leída'}
                        style={{
                          background: notification.unread 
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        {notification.unread ? (
                          <IconPoint size={12} />
                        ) : (
                          <IconCheck size={12} />
                        )}
                      </ActionIcon>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        radius="xl"
                        onClick={(e) => { e.stopPropagation(); toggleSaved(notification.id); }}
                        title={notification.saved ? 'Unsave' : 'Save'}
                        style={{
                          background: notification.saved 
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        {notification.saved ? <IconStarFilled size={12} /> : <IconStar size={12} />}
                      </ActionIcon>
                    </Group>
                  </Group>
                </Paper>
              );
            })}
              </Stack>
            ))
          )}
        </Stack>
        </ScrollArea>
      </Box>
    </Box>
  );
};

export default NotificacionesPage;
